'use client';

/**
 * Posthog Analytics Provider Component - LAZY LOADED FOR PERFORMANCE
 *
 * Wraps the application with Posthog analytics tracking.
 * Must be a client component since Posthog requires client-side functionality.
 *
 * Performance Optimization:
 * - PostHog is lazy-loaded AFTER first meaningful paint (3 seconds delay)
 * - OR immediately on first user interaction (click, scroll, keypress)
 * - This saves ~173 KiB and ~280ms on initial page load
 * - Improves LCP (Largest Contentful Paint) significantly
 *
 * Features:
 * - Initializes Posthog with API key and host
 * - Tracks pageviews automatically
 * - Identifies users when logged in
 * - Only loads when environment variables are configured
 *
 * Usage:
 * Already included in app/layout.tsx - no need to use directly
 */

import { useEffect, useState, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useSession } from '@/lib/platform/auth/client';
import type { PostHog } from 'posthog-js';

interface PosthogProviderProps {
  children: React.ReactNode;
}

export default function PosthogProviderWrapper({ children }: PosthogProviderProps) {
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com';
  const [posthogLoaded, setPosthogLoaded] = useState(false);
  const [posthogInstance, setPosthogInstance] = useState<PostHog | null>(null);

  // Detect if running in E2E test environment
  const isE2ETest =
    typeof window !== 'undefined' &&
    (window.navigator.webdriver || // Playwright/Selenium sets this
      window.location.hostname === 'localhost'); // E2E tests run on localhost

  // Lazy-load PostHog after delay or user interaction
  useEffect(() => {
    if (typeof window === 'undefined' || !posthogKey || isE2ETest || posthogLoaded) {
      return;
    }

    let isInitialized = false;

    const initPosthog = async () => {
      if (isInitialized) return;
      isInitialized = true;

      try {
        // Dynamic import PostHog (saves ~173 KiB on initial load)
        const posthogModule = await import('posthog-js');
        const posthog = posthogModule.default;

        posthog.init(posthogKey, {
          api_host: posthogHost,
          person_profiles: 'identified_only', // Only create profiles for identified users
          capture_pageview: false, // We'll manually track pageviews for better control
          capture_pageleave: true, // Track when users leave pages
          disable_surveys: true, // Don't load surveys.js (saves 23KB + 120ms)
          autocapture: {
            // Capture clicks, form submissions, and change events
            dom_event_allowlist: ['click', 'submit', 'change'],
          },
          loaded: (ph) => {
            // Disable session recording — causes cross-origin iframe errors
            // when trust center is embedded via iframe on valiyou.com
            ph.stopSessionRecording();
            setPosthogInstance(posthog);
            setPosthogLoaded(true);
          },
        });
      } catch {
        // Silently fail if PostHog fails to load
        // Don't block the app or spam console
      }
    };

    // Strategy 1: Initialize after 3 seconds (if user hasn't interacted yet)
    const timer = setTimeout(() => {
      initPosthog();
    }, 3000);

    // Strategy 2: Initialize immediately on first user interaction
    const handleInteraction = () => {
      clearTimeout(timer);
      initPosthog();
      // Remove listeners after first interaction
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };

    window.addEventListener('click', handleInteraction, { once: true, passive: true });
    window.addEventListener('scroll', handleInteraction, { once: true, passive: true });
    window.addEventListener('keydown', handleInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', handleInteraction, { once: true, passive: true });

    return () => {
      clearTimeout(timer);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
    };
  }, [posthogKey, posthogHost, isE2ETest, posthogLoaded]);

  // IMPORTANT: Always render {children} in the same tree position, regardless
  // of PostHog load state. Previously this component swapped its returned
  // element type (Fragment → PosthogProviderInner → PostHogProvider) when
  // PostHog finished loading, which caused React to unmount and remount the
  // ENTIRE app tree twice (~1-3s after page load) — re-running chart
  // animations and blinking the UI. The posthog-js/react PostHogProvider
  // context was removed entirely because no code in the codebase consumes it;
  // the trackers below receive the posthog instance via props instead.
  return (
    <>
      {posthogLoaded && posthogInstance && (
        <>
          <Suspense fallback={null}>
            <PageViewTracker posthog={posthogInstance} />
          </Suspense>
          <UserIdentifier posthog={posthogInstance} />
        </>
      )}
      {children}
    </>
  );
}

/**
 * Track pageviews on route changes
 */
function PageViewTracker({ posthog }: { posthog: PostHog }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;
      if (searchParams && searchParams.toString()) {
        url = url + `?${searchParams.toString()}`;
      }

      posthog.capture('$pageview', {
        $current_url: url,
      });
    }
  }, [pathname, searchParams, posthog]);

  return null;
}

/**
 * Identify users when logged in
 */
function UserIdentifier({ posthog }: { posthog: PostHog }) {
  const { data: session } = useSession();

  useEffect(() => {
    if (!posthog) return;

    if (session?.user) {
      // Identify user in Posthog
      posthog.identify(session.user.id, {
        email: session.user.email,
        name: session.user.name,
        createdAt: session.user.createdAt,
      });
    } else {
      // Reset user identity when logged out
      posthog.reset();
    }
  }, [session, posthog]);

  return null;
}
